// Considered: PropertyFilterMolecule (the collapsible filter-chip list) — rejected
// because it has no drill mode: clicking a chip always expands the editor inline,
// which is exactly the reflow the rail drill-in (SC1) exists to stop. This molecule
// adds the summary/drill(onEdit)/inline modes on top of FilterEditorPanelMolecule
// without duplicating its editing body. See spec context-selector-editor.md Phase 1
// (SC8) Reuse Audit.
import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useEditorCap } from '../../../hooks/useEditorCap';
import type { FilterExpression } from 'types/filters';
import {
  VALUELESS_OPERATORS,
  getFilterOperatorOptions,
  isFilterExpressionValid,
} from '../../../utils/filterOperators';
import { summarizeFilterValue } from '../../../utils/filterLaneSummaries';
import FilterEditorPanelMolecule from '../FilterEditorPanel/FilterEditorPanelMolecule';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import {
  getFilterTypeAccentVar,
  getFilterTypeIconClassName,
} from '../../../utils/filterTypeVisuals';
import LaneMarkerAtom from '../../atoms/LaneMarker/LaneMarkerAtom';
import type { FilterLaneKind } from '../../../types/filterLane';
import './EditableFilterRowMolecule.scss';

/** The closed row's value bit: an invalid placeholder, the summary text, or nothing when there's neither. */
function renderValueSummary(
  invalid: boolean,
  showsValuelessSummary: boolean,
  valueSummary: string,
  summaryValueDatumId: string | undefined,
  t: (key: string) => string
): React.ReactNode {
  if (!showsValuelessSummary && invalid) {
    return (
      <span className="editable-filter-row-molecule__value editable-filter-row-molecule__value--invalid">
        {t('filterEditor.row.setValuePlaceholder')}
      </span>
    );
  }
  if (!valueSummary) return null;
  return (
    <span
      className="editable-filter-row-molecule__value"
      data-datum={summaryValueDatumId}
      data-kind={summaryValueDatumId ? 'value' : undefined}
    >
      {valueSummary}
    </span>
  );
}

/** The closed row's one-line read-back: glyph · property · operator · value. Lane
 * rows (arc/relationship/perspective) fold their operator into the value summary. */
function renderSummaryNode(
  prop: PropertyOption | undefined,
  t: (key: string) => string,
  operatorLabel: string,
  layout: 'row' | 'narrow',
  valueSummaryNode: React.ReactNode,
  choosePropertyLabel: string | undefined
): React.ReactNode {
  if (!prop) {
    return (
      <span className="editable-filter-row-molecule__placeholder" data-testid="editable-filter-row-placeholder">
        <i className="pi pi-plus-circle" aria-hidden="true" />
        {choosePropertyLabel ?? t('filterEditor.row.choosePropertyPlaceholder')}
      </span>
    );
  }
  return (
    <>
      <i className={getFilterTypeIconClassName(prop.type)} aria-hidden="true" />
      <span className="editable-filter-row-molecule__prop-label">{prop.label}</span>
      {prop.type !== 'arc' && prop.type !== 'relationship' && prop.type !== 'perspective' && (
        <span className="editable-filter-row-molecule__operator">{operatorLabel}</span>
      )}
      {layout !== 'narrow' && valueSummaryNode}
    </>
  );
}

interface DisclosureState {
  showEditor: boolean;
  disclosureProps: { 'aria-expanded'?: boolean; 'aria-controls'?: string };
  toggleIconName: string;
}

/**
 * In place, the summary and chevron are a disclosure for the inline editor. In
 * drill mode they hand off to another pane (`onEdit`), so they expose no
 * expanded state of their own and never show the inline editor.
 */
function getDisclosureState(drill: boolean, open: boolean, editorId: string): DisclosureState {
  if (drill) {
    return { showEditor: false, disclosureProps: {}, toggleIconName: 'pi-sliders-h' };
  }
  if (!open) {
    return {
      showEditor: false,
      disclosureProps: { 'aria-expanded': false, 'aria-controls': undefined },
      toggleIconName: 'pi-chevron-down',
    };
  }
  return {
    showEditor: true,
    disclosureProps: { 'aria-expanded': true, 'aria-controls': editorId },
    toggleIconName: 'pi-chevron-up',
  };
}

/** The root's state/parity data attributes and the property type's accent colour. */
function getRootAttributes(
  showInvalid: boolean,
  parityBlock: 'uf-row' | 'uf-types' | 'uf-column',
  prop: PropertyOption | undefined
): {
  'data-invalid'?: 'true';
  'data-datum'?: string;
  'data-kind'?: string;
  style?: React.CSSProperties;
} {
  const attributes: ReturnType<typeof getRootAttributes> = {};
  if (showInvalid) attributes['data-invalid'] = 'true';
  if (parityBlock === 'uf-types') {
    attributes['data-datum'] = 'uf-types.card#2';
    attributes['data-kind'] = 'card';
  }
  if (prop) attributes.style = { '--efr-accent': getFilterTypeAccentVar(prop.type) } as React.CSSProperties;
  return attributes;
}

function buildRootClassName(opts: {
  showEditor: boolean;
  showInvalid: boolean;
  hasProblem: boolean;
  layout: 'row' | 'narrow';
  className: string;
}): string {
  return [
    'editable-filter-row-molecule',
    opts.showEditor ? 'editable-filter-row-molecule--editing' : '',
    opts.showInvalid ? 'editable-filter-row-molecule--invalid' : '',
    opts.hasProblem ? 'editable-filter-row-molecule--problem' : '',
    opts.layout === 'narrow' ? 'editable-filter-row-molecule--narrow' : '',
    opts.className,
  ]
    .filter(Boolean)
    .join(' ');
}

export interface EditableFilterRowMoleculeProps {
  filter: FilterExpression;
  availableProperties: PropertyOption[];
  onChange: (filter: FilterExpression) => void;
  onRemove?: () => void;
  /**
   * Fired when focus lands anywhere inside this row's open value editor —
   * the host's "focused row" for variable-insertion gating (`@today` only
   * offered while a date row is focused, etc; EAINT-936 Phase 1). Never
   * cleared on blur: like `justAddedLeafId`, focus is sticky so clicking a
   * rail button (which blurs the input first) doesn't lose it.
   */
  onValueFocus?: (filter: FilterExpression) => void;
  readOnly?: boolean;
  excludePropertyTypes?: PropertyType[];
  /** Sphere-type names the in-row property picker is scoped to — forwarded to the catalog's scope banner. */
  scopeTypeLabels?: string[];
  /** Published arc templates — forwarded to the `type==='arc'` value editor (SC2). */
  arcTemplates?: ArcTemplateOption[];
  /** Available relationships — forwarded to the `type==='relationship'` value editor (EAINT-725 SC3). */
  availableRelationships?: RelationshipOption[];
  /** Schemas for the Sphere Type/Family chip-selector value editor. */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Available perspectives — forwarded to the `type==='perspective'` value editor (EAINT-725 Phase 4a). */
  availablePerspectives?: PerspectiveOption[];
  /** Props for `PerspectiveSearch` (assignee lookup) — forwarded to the `type==='perspective'` value editor. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * When supplied, the row renders summary-only; clicking it (or the edit
   * affordance) calls `onEdit` instead of expanding an inline editor — the host
   * opens a focused drill-in overlay elsewhere (e.g. the rail). This is what
   * stops filter editing from reflowing the surrounding UI (SC1).
   */
  onEdit?: () => void;
  /**
   * Caller-supplied wording. The default placeholder asks for an ASPECT, which is the
   * context builder's vocabulary; a rule scoping a relationship is choosing a sphere
   * PROPERTY. Same seam, and same reason, as `ApplicabilityEditorMolecule`'s `copy`.
   */
  copy?: {
    /** Replaces "Choose an aspect…" on a row with nothing chosen yet. */
    chooseProperty?: string;
  };
  className?: string;
  /**
   * Which of the six unified-filter lanes this row belongs to (a property
   * leaf renders under `aspect` or `identity`; lane conditions render via
   * `LaneFilterRowMolecule` instead). Omitted — today's render, with no
   * leading marker — every pre-existing caller (context/segment filters)
   * predates the lane concept and stays unmarked.
   */
  lane?: FilterLaneKind;
  /**
   * Opens the editor on mount even when a property is already chosen — a row
   * just added (drill-in "Add condition") should open straight to its value
   * instead of making the user click the closed summary first
   * (filter-row.jsx:708-713, `EditableFilterRow`'s own `defaultOpen`). Ignored
   * in drill mode (`onEdit` supplied), which never shows an inline editor.
   *
   * Opening is not focusing: a host that opens EVERY row on mount (the context
   * editor's flat lists) must not have each one grab focus in turn — see
   * `autoFocusValue`, which only the row just added sets.
   */
  defaultOpen?: boolean;
  /**
   * Moves focus into the open editor's value control on mount — only for the
   * one row the author just added (spec § Keyboard map: "a new row focuses
   * its value editor"). Separate from `defaultOpen` because a host that opens
   * every row on mount would otherwise leave focus on whichever row mounted
   * last. Ignored unless the editor is open on mount.
   */
  autoFocusValue?: boolean;
  /**
   * A host-detected contradiction with a locked tier (e.g. this row's
   * condition can never match given a context/view filter above it). Renders
   * an alert glyph leading the summary (its `title`) and the same message
   * under the row — both closed and open — never blocking editing
   * (filter-row.jsx:708,744-746,756-760).
   */
  problem?: string;
  /**
   * `'narrow'` splits the summary into two lines — condition (glyph, property,
   * operator) then the value read-back full width beneath it — for hosts too
   * narrow for one line to hold all four without truncating unpredictably
   * (advanced-filters-v2.jsx `AFNarrowRow`, used for identity rows today).
   * Defaults to `'row'`, today's single-line summary.
   */
  layout?: 'row' | 'narrow';
  /**
   * The parity block this row is standing in for — `uf-row` (the default,
   * inline/drill/panel row states) or `uf-types` (the value-editors gallery,
   * one row per property kind). Both blocks share this molecule as their
   * `implSectionId`; the prop only changes which block's datum ids the row's
   * card/toggle/remove affordances answer to.
   */
  parityBlock?: 'uf-row' | 'uf-types' | 'uf-column';
  /**
   * Parity instrumentation only — overrides the toggle/remove buttons' default
   * `${parityBlock}.button`/`${parityBlock}.button#2` ids. The prototype's B1
   * artboard renders `EditableFilterRow` twice (inline mode, drill mode) as two
   * separate cards, so the drill-mode instance's toggle/remove are a distinct
   * occurrence (`uf-row.button#17`/`uf-row.button#18`) from the inline card's.
   * Omitted everywhere except the dedicated drill-mode parity story.
   */
  toggleDatumId?: string;
  removeDatumId?: string;
  /**
   * Parity instrumentation only — the summary button itself (the control that opens the
   * condition). No default: only a host whose decomposition names it (the type composer's
   * relationship scope, `context/filter-row.jsx:680-700`) supplies one.
   */
  summaryDatumId?: string;
  /**
   * Parity instrumentation only — the closed row's own summary value span
   * (uf-column's `.value#2`, `filter-row.jsx:723`) has no id in every other
   * caller, since B1's row summary was never itself a matched datum there.
   */
  summaryValueDatumId?: string;
  /**
   * Parity instrumentation only — forwarded straight through to the inner
   * `FilterEditorPanelMolecule`'s own override props of the same shape, plus
   * this row's own Apply footer (`applyDatumId`, default `${parityBlock}.apply`).
   * B1's artboard renders two inline demo cards with the editor open, so the
   * second card's occurrence needs every id numbered (`.property#2`/`.condition#2`/
   * `.op#2`/`.value#4`/`.apply#2`) to avoid colliding with the first card's
   * block defaults. Omitted everywhere except that dedicated parity story.
   */
  propertyDatumId?: string;
  conditionDatumId?: string;
  opDatumId?: string;
  valueDatumId?: string;
  applyDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  textDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  numberDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  numberMinDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  numberMaxDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  propertySummaryButtonDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  propertySummaryGlyphDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own `arc*DatumId` override props of the same shape (in turn forwarded to `ArcValueEditorMolecule`). */
  arcTriggerButtonDatumId?: string;
  arcTriggerGlyphDatumId?: string;
  arcMenuButtonDatumId?: string;
  arcMenuGlyphDatumId?: string;
  arcTabsDatumId?: string;
  arcHealthLabelDatumId?: string;
  arcHealthChipDatumId?: string;
  arcStageLabelDatumId?: string;
  arcStageChipDatumId?: string;
  arcLifecycleLabelDatumId?: string;
  arcLifecycleChipDatumId?: string;
  arcAtLeastLabelDatumId?: string;
  arcMinCountDecreaseDatumId?: string;
  arcMinCountIncreaseDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  multiChipMeDatumId?: string;
  /** Parity instrumentation only — forwarded to `FilterEditorPanelMolecule`'s own override prop of the same shape. */
  multiChipDatumId?: string;
  /**
   * Replaces the inner `FilterEditorPanelMolecule`'s default `AspectCatalogMolecule`
   * with a caller-supplied picker (e.g. `AspectCatalogMolecule` scoped to this row's
   * own `uf-row.*` datum ids) — forwarded straight through to the panel's own slot
   * of the same shape. See `FilterEditorPanelMolecule`'s `renderPropertyPicker` doc.
   */
  renderPropertyPicker?: (opts: {
    current: string | null;
    onPick: (key: string) => void;
    onCancel?: () => void;
  }) => React.ReactNode;
  /**
   * Hides the editor footer's Apply button — column popovers auto-commit on
   * every value change (BLOCKER 1, EAINT-936 Phase 2) so a separate Apply
   * step would hold an edit uncommitted after the user already sees it land.
   * The editor still closes on its own (host-driven, via the auto-commit
   * effect), not on this button.
   */
  hideApplyButton?: boolean;
}

const EditableFilterRowMolecule: React.FC<EditableFilterRowMoleculeProps> = ({
  filter,
  availableProperties,
  onChange,
  onRemove,
  onValueFocus,
  hideApplyButton = false,
  readOnly = false,
  excludePropertyTypes,
  scopeTypeLabels,
  arcTemplates,
  availableRelationships,
  familyTypeSchemas,
  availablePerspectives,
  perspectiveSearchProps,
  onEdit,
  copy,
  className = '',
  lane,
  defaultOpen = false,
  autoFocusValue = false,
  problem,
  layout = 'row',
  parityBlock = 'uf-row',
  toggleDatumId,
  removeDatumId,
  summaryDatumId,
  summaryValueDatumId,
  propertyDatumId,
  conditionDatumId,
  opDatumId,
  valueDatumId,
  applyDatumId,
  textDatumId,
  numberDatumId,
  numberMinDatumId,
  numberMaxDatumId,
  propertySummaryButtonDatumId,
  propertySummaryGlyphDatumId,
  arcTriggerButtonDatumId,
  arcTriggerGlyphDatumId,
  arcMenuButtonDatumId,
  arcMenuGlyphDatumId,
  arcTabsDatumId,
  arcHealthLabelDatumId,
  arcHealthChipDatumId,
  arcStageLabelDatumId,
  arcStageChipDatumId,
  arcLifecycleLabelDatumId,
  arcLifecycleChipDatumId,
  arcAtLeastLabelDatumId,
  arcMinCountDecreaseDatumId,
  arcMinCountIncreaseDatumId,
  multiChipMeDatumId,
  multiChipDatumId,
  renderPropertyPicker,
}) => {
  const { t, i18n } = useTranslation();
  const drill = typeof onEdit === 'function';
  const [open, setOpen] = useState(!drill && (defaultOpen || !filter.property));

  const prop = filter.property ? availableProperties.find((p) => p.key === filter.property) : undefined;
  const valueSummary = summarizeFilterValue(
    filter,
    t,
    prop,
    { availableRelationships, availablePerspectives },
    i18n?.language
  );
  // Mirrors filter-row.jsx's `efFilterValid` — a row with a property but no value
  // entered for a value-requiring operator is invalid; it marks the closed summary
  // in error styling and blocks Apply until fixed.
  const invalid = !isFilterExpressionValid(filter, prop);
  const showsValuelessSummary = prop ? VALUELESS_OPERATORS.includes(filter.operator) : false;

  // Resolve the operator through the same typed+localized derivation the editor
  // uses, so the summary never prints the raw canonical token to the user.
  const operatorLabel =
    getFilterOperatorOptions(prop?.type ?? 'text', t).find((o) => o.value === filter.operator)
      ?.label ?? filter.operator;
  const editorRef = useRef<HTMLDivElement>(null);
  const editorId = useId();
  const { showEditor, disclosureProps, toggleIconName } = getDisclosureState(drill, open, editorId);
  const { maxHeight: editorMaxHeight } = useEditorCap(editorRef, showEditor);

  const handleSummaryClick = () => {
    if (readOnly) return;
    if (drill) onEdit?.();
    else setOpen((o) => !o);
  };

  const showInvalid = !showEditor && invalid;

  const valueSummaryNode = renderValueSummary(invalid, showsValuelessSummary, valueSummary, summaryValueDatumId, t);

  const summaryNode = renderSummaryNode(prop, t, operatorLabel, layout, valueSummaryNode, copy?.chooseProperty);

  // `layout='narrow'` moves the value out of the condition line entirely —
  // rendered full width on its own line below instead of clipping inline.
  const narrowValue = layout === 'narrow' && prop ? valueSummaryNode : null;

  const rootClassName = buildRootClassName({ showEditor, showInvalid, hasProblem: Boolean(problem), layout, className });

  return (
    <div
      className={rootClassName}
      // Every host lists its conditions — the row is one of the `each(filters)` repeat.
      data-container="each(filters)"
      data-testid="editable-filter-row"
      {...getRootAttributes(showInvalid, parityBlock, prop)}
    >
      <div className="editable-filter-row-molecule__summary">
        {problem && (
          <i
            className="pi pi-exclamation-triangle editable-filter-row-molecule__problem-icon"
            title={problem}
            aria-hidden="true"
            data-testid="editable-filter-row-problem-icon"
          />
        )}
        {lane && <LaneMarkerAtom lane={lane} size="md" />}
        <button
          type="button"
          className="editable-filter-row-molecule__summary-btn"
          onClick={handleSummaryClick}
          disabled={readOnly}
          {...(readOnly ? {} : disclosureProps)}
          data-testid="editable-filter-row-summary"
          data-datum={summaryDatumId}
          data-kind={summaryDatumId ? 'button' : undefined}
        >
          {summaryNode}
        </button>
        {!readOnly && (
          <button
            type="button"
            className="editable-filter-row-molecule__toggle"
            onClick={handleSummaryClick}
            aria-label={drill ? t('filterEditor.row.editFilter') : t('filterEditor.row.toggleFilter')}
            {...disclosureProps}
            data-testid="editable-filter-row-toggle"
            data-datum={toggleDatumId ?? `${parityBlock}.button`}
            data-kind="button"
          >
            <i className={`pi ${toggleIconName}`} aria-hidden="true" />
          </button>
        )}
        {!readOnly && onRemove && (
          <button
            type="button"
            className="editable-filter-row-molecule__remove"
            onClick={onRemove}
            aria-label={t('filterEditor.row.removeFilter')}
            data-testid="editable-filter-row-remove"
            data-datum={removeDatumId ?? `${parityBlock}.button#2`}
            data-kind="button"
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </div>

      {narrowValue && (
        <div className="editable-filter-row-molecule__value-row" data-testid="editable-filter-row-value-row">
          {narrowValue}
        </div>
      )}

      {problem && (
        <div className="editable-filter-row-molecule__problem" data-testid="editable-filter-row-problem">
          {problem}
        </div>
      )}

      {showEditor && (
        <div
          id={editorId}
          className="editable-filter-row-molecule__editor"
          ref={editorRef}
          data-testid="editable-filter-row-editor"
          style={editorMaxHeight !== undefined ? { maxHeight: editorMaxHeight, overflowY: 'auto' } : undefined}
          onFocus={onValueFocus ? () => onValueFocus(filter) : undefined}
        >
          <FilterEditorPanelMolecule
            filter={filter}
            availableProperties={availableProperties}
            onChange={onChange}
            onRemove={onRemove}
            readOnly={readOnly}
            excludePropertyTypes={excludePropertyTypes}
            scopeTypeLabels={scopeTypeLabels}
            arcTemplates={arcTemplates}
            availableRelationships={availableRelationships}
            availablePerspectives={availablePerspectives}
            familyTypeSchemas={familyTypeSchemas}
            perspectiveSearchProps={perspectiveSearchProps}
            // Forward the row's own parityBlock straight through — B1's row
            // (`uf-row`) and B3's per-type gallery card (`uf-types`) each
            // host this exact `<FilterEditorPanel>` render once, so the
            // panel's Condition/Value/value-editor datum ids must answer to
            // whichever block this instance is standing in for, not always
            // `uf-row` (that collapsed B3's `uf-types.op` onto B1's
            // `uf-row.op` and broke the parity match for both).
            parityBlock={parityBlock}
            propertyEyebrowDatumId={propertyDatumId}
            conditionDatumId={conditionDatumId}
            opDatumId={opDatumId}
            valueDatumId={valueDatumId}
            textDatumId={textDatumId}
            numberDatumId={numberDatumId}
            numberMinDatumId={numberMinDatumId}
            numberMaxDatumId={numberMaxDatumId}
            propertySummaryButtonDatumId={propertySummaryButtonDatumId}
            propertySummaryGlyphDatumId={propertySummaryGlyphDatumId}
            arcTriggerButtonDatumId={arcTriggerButtonDatumId}
            arcTriggerGlyphDatumId={arcTriggerGlyphDatumId}
            arcMenuButtonDatumId={arcMenuButtonDatumId}
            arcMenuGlyphDatumId={arcMenuGlyphDatumId}
            arcTabsDatumId={arcTabsDatumId}
            arcHealthLabelDatumId={arcHealthLabelDatumId}
            arcHealthChipDatumId={arcHealthChipDatumId}
            arcStageLabelDatumId={arcStageLabelDatumId}
            arcStageChipDatumId={arcStageChipDatumId}
            arcLifecycleLabelDatumId={arcLifecycleLabelDatumId}
            arcLifecycleChipDatumId={arcLifecycleChipDatumId}
            arcAtLeastLabelDatumId={arcAtLeastLabelDatumId}
            arcMinCountDecreaseDatumId={arcMinCountDecreaseDatumId}
            arcMinCountIncreaseDatumId={arcMinCountIncreaseDatumId}
            multiChipMeDatumId={multiChipMeDatumId}
            multiChipDatumId={multiChipDatumId}
            renderPropertyPicker={renderPropertyPicker}
            autoFocusValue={autoFocusValue}
          />
          {!readOnly && !hideApplyButton && (
            <div className="editable-filter-row-molecule__editor-footer">
              <button
                type="button"
                className="editable-filter-row-molecule__apply"
                onClick={() => setOpen(false)}
                disabled={invalid}
                data-testid="editable-filter-row-apply"
                data-datum={applyDatumId ?? `${parityBlock}.apply`}
                data-kind="button"
              >
                <i className="pi pi-check" aria-hidden="true" />
                {t('filterEditor.row.apply')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableFilterRowMolecule;
